// src/hooks/notifications/useGlobalThreadNotifications.js
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ThreadNotificationHandler } from "../../utils/notification/threadNotificationHandler";
import { useSharedEventBridgeNotifications } from "./useSharedEventBridgeNotifications";

/**
 * Global thread notification handler for use in the main App component
 * Handles thread notifications when not in a ThreadProvider context
 */
export const useGlobalThreadNotifications = () => {
  const navigate = useNavigate();

  // Use shared EventBridge notifications
  const { notifications } = useSharedEventBridgeNotifications();

  // Handle thread notifications globally
  const handleGlobalThreadNotification = useCallback((notification) => {
    if (!ThreadNotificationHandler.isThreadNotification(notification)) {
      return;
    }

    // Update document title for thread notifications
    if (document.title && !document.title.includes("(")) {
      document.title = `(1) ${document.title}`;
    }

    // Handle navigation if user clicks on browser notification
    // This is handled by the browser notification click event
  }, []);

  // Listen for browser notification clicks
  useEffect(() => {
    const handleNotificationClick = (event) => {
      if (event.notification && event.notification.data) {
        const notificationData = event.notification.data;

        if (
          ThreadNotificationHandler.isThreadNotification({
            type: notificationData.type,
          })
        ) {
          const handler = new ThreadNotificationHandler(navigate);
          const { threadId, messageId, replyId } = notificationData;

          if (threadId) {
            handler.navigateToThread(threadId, messageId, replyId);
          }
        }
      }
    };

    // Listen for notification click events
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "notificationclick",
        handleNotificationClick
      );
    }

    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "notificationclick",
          handleNotificationClick
        );
      }
    };
  }, [navigate]);

  // Listen for EventBridge notifications
  useEffect(() => {
    if (notifications && notifications.length > 0) {
      // Process the most recent notification
      const latestNotification = notifications[0];
      if (ThreadNotificationHandler.isThreadNotification(latestNotification)) {
        handleGlobalThreadNotification(latestNotification);
      }
    }
  }, [notifications, handleGlobalThreadNotification]);

  return {
    handleGlobalThreadNotification,
    isGlobalHandlerActive: true,
  };
};

export default useGlobalThreadNotifications;
